import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ScanLine, Camera, Upload, Loader2, Check, RotateCcw,
  User, Mail, Phone, Building2, Briefcase, Globe, MapPin, QrCode, X
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// Hoisted to module scope — defining a component INSIDE another component
// causes React to remount the input on every parent render, which is the exact
// "letter-by-letter typing" bug. Always declare components at module scope.
const Field = ({ icon: Icon, label, field, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <Label className="text-[10px] text-gray-500 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</Label>
    <Input
      type={type}
      value={value || ''}
      onChange={e => onChange(field, e.target.value)}
      placeholder={placeholder}
      className="h-8 text-sm"
      data-testid={`scan-field-${field}`}
    />
  </div>
);

export default function CardScannerPage() {
  const [mode, setMode] = useState('choose'); // choose | camera | qr | review
  const [imagePreview, setImagePreview] = useState(null);
  const [editData, setEditData] = useState({});
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const qrScannerRef = useRef(null);
  const navigate = useNavigate();

  const emptyContact = { first_name: '', last_name: '', email: '', phone: '', company: '', job_title: '', website: '', address: '', industry: '', area: '', city: '', country: '', num_employees: '', notes: '' };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
      if (qrScannerRef.current) {
        try { qrScannerRef.current.stop(); } catch {}
      }
    };
  }, [cameraStream]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      setCameraStream(stream);
      setMode('camera');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      toast.error('Camera access denied. Please allow camera permissions.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    const dataUrl = c.toDataURL('image/jpeg', 0.9);
    setImagePreview(dataUrl);
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    processImage(dataUrl);
  };

  const handleFileUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image file'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      processImageFile(file);
    };
    reader.readAsDataURL(file);
  };

  const processImageFile = async (file) => {
    setScanning(true);
    setMode('review');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/leads/scan-card`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success) {
        setEditData({ ...emptyContact, ...data.extracted });
        toast.success('Business card scanned!');
      } else {
        setEditData({ ...emptyContact });
        toast.error('Could not extract data. Fill manually.');
      }
    } catch { setEditData({ ...emptyContact }); toast.error('Scan failed. Fill manually.'); }
    finally { setScanning(false); }
  };

  const processImage = async (dataUrl) => {
    setScanning(true);
    setMode('review');
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/leads/scan-card`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success) {
        setEditData({ ...emptyContact, ...data.extracted });
        toast.success('Business card scanned!');
      } else {
        setEditData({ ...emptyContact });
        toast.error('Could not extract. Fill manually.');
      }
    } catch { setEditData({ ...emptyContact }); toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  const startQrScanner = async () => {
    setMode('qr');
    setTimeout(async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('qr-reader');
        qrScannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (text) => {
            scanner.stop().catch(() => {});
            qrScannerRef.current = null;
            parseQrData(text);
          },
          () => {}
        );
      } catch (err) {
        toast.error('QR scanner failed. Check camera permissions.');
        setMode('choose');
      }
    }, 200);
  };

  const parseQrData = (text) => {
    const contact = { ...emptyContact, source: 'qr_scan' };
    if (text.includes('BEGIN:VCARD') || text.includes('begin:vcard')) {
      // Parse vCard
      const lines = text.split(/\r?\n/);
      for (const line of lines) {
        const lower = line.toLowerCase();
        if (lower.startsWith('fn:') || lower.startsWith('fn;')) {
          const name = line.split(':').slice(1).join(':').trim();
          const parts = name.split(' ');
          contact.first_name = parts[0] || '';
          contact.last_name = parts.slice(1).join(' ') || '';
        } else if (lower.startsWith('n:') || lower.startsWith('n;')) {
          const val = line.split(':').slice(1).join(':');
          const parts = val.split(';');
          if (!contact.last_name) contact.last_name = (parts[0] || '').trim();
          if (!contact.first_name) contact.first_name = (parts[1] || '').trim();
        } else if (lower.startsWith('email')) {
          contact.email = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('tel')) {
          contact.phone = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('org')) {
          contact.company = line.split(':').slice(1).join(':').replace(/;/g, ' ').trim();
        } else if (lower.startsWith('title')) {
          contact.job_title = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('url')) {
          contact.website = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('adr')) {
          const adr = line.split(':').slice(1).join(':').split(';');
          contact.address = adr.filter(Boolean).join(', ').trim();
        }
      }
      toast.success('Contact QR scanned!');
    } else if (text.includes('@')) {
      contact.email = text.trim();
      toast.success('Email detected from QR');
    } else if (text.match(/^\+?[\d\s()-]+$/)) {
      contact.phone = text.trim();
      toast.success('Phone detected from QR');
    } else {
      contact.notes = text;
      toast.info('QR data captured. Review and fill details.');
    }
    setEditData(contact);
    setMode('review');
  };

  const saveContact = async () => {
    if (!editData.first_name?.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...editData,
        source: editData.source || (imagePreview ? 'card_scan' : 'manual'),
        stage: 'new',
        deal_value: 0,
        tags: [],
        opted_in: true,
      };
      await axios.post(`${API}/api/leads`, payload);
      toast.success('Contact saved to CRM!');
      navigate('/leads/pipeline');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const reset = () => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    if (qrScannerRef.current) { try { qrScannerRef.current.stop(); } catch {} }
    setCameraStream(null);
    qrScannerRef.current = null;
    setMode('choose');
    setImagePreview(null);
    setEditData({});
    setScanning(false);
  };

  // Stable callback so the hoisted Field never re-mounts unnecessarily.
  const handleFieldChange = useCallback((field, value) => {
    setEditData(p => ({ ...p, [field]: value }));
  }, []);

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="card-scanner-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center"><ScanLine className="h-5 w-5 text-violet-600" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Contact Scanner<PageInfoTooltip pageKey="contact-scanner" /></h1>
          <p className="text-xs text-gray-400">Scan business cards or QR codes to add contacts</p>
        </div>
        {mode !== 'choose' && <Button variant="outline" size="sm" className="ml-auto gap-1" onClick={reset} data-testid="scanner-reset"><RotateCcw className="h-3.5 w-3.5" />Start Over</Button>}
      </div>

      {mode === 'choose' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-2 border-dashed border-violet-200 hover:border-violet-400 cursor-pointer transition-colors group" onClick={startCamera} data-testid="scan-card-camera">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-violet-200 transition-colors"><Camera className="h-7 w-7 text-violet-600" /></div>
              <h3 className="text-sm font-bold text-gray-800 mb-1">Scan Business Card</h3>
              <p className="text-[10px] text-gray-400">Use your camera to scan a physical business card. AI extracts all contact details.</p>
              <Badge className="bg-violet-100 text-violet-700 text-[9px] mt-2">AI OCR</Badge>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-blue-200 hover:border-blue-400 cursor-pointer transition-colors group" onClick={startQrScanner} data-testid="scan-qr-option">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-200 transition-colors"><QrCode className="h-7 w-7 text-blue-600" /></div>
              <h3 className="text-sm font-bold text-gray-800 mb-1">Scan Contact QR</h3>
              <p className="text-[10px] text-gray-400">Scan a QR code from someone's phone contacts. Instantly imports their vCard.</p>
              <Badge className="bg-blue-100 text-blue-700 text-[9px] mt-2">vCard</Badge>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-gray-200 hover:border-gray-400 cursor-pointer transition-colors group" onClick={() => fileRef.current?.click()} data-testid="scan-upload-option">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-gray-200 transition-colors"><Upload className="h-7 w-7 text-gray-600" /></div>
              <h3 className="text-sm font-bold text-gray-800 mb-1">Upload Card Image</h3>
              <p className="text-[10px] text-gray-400">Upload a photo of a business card from your gallery. AI will extract details.</p>
              <Badge className="bg-gray-100 text-gray-700 text-[9px] mt-2">Gallery</Badge>
            </CardContent>
          </Card>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }} />
        </div>
      )}

      {mode === 'camera' && (
        <Card className="border-gray-200 overflow-hidden">
          <CardContent className="p-0 relative">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-[400px] object-cover bg-black" data-testid="camera-viewfinder" />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[280px] h-[170px] border-2 border-white/60 rounded-xl" />
            </div>
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
              <Button onClick={capturePhoto} className="bg-white text-gray-900 hover:bg-gray-100 rounded-full w-14 h-14 shadow-lg" data-testid="capture-photo-btn">
                <Camera className="h-6 w-6" />
              </Button>
              <Button variant="outline" onClick={reset} className="bg-white/80 rounded-full w-10 h-10"><X className="h-5 w-5" /></Button>
            </div>
            <p className="absolute top-3 left-0 right-0 text-center text-white text-xs bg-black/30 py-1">Position business card within the frame</p>
          </CardContent>
        </Card>
      )}

      {mode === 'qr' && (
        <Card className="border-gray-200 overflow-hidden">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 mb-3 text-center">Point your camera at a contact QR code</p>
            <div id="qr-reader" className="mx-auto max-w-[400px] rounded-lg overflow-hidden" data-testid="qr-scanner-area" />
            <Button variant="outline" className="mt-3 mx-auto block" onClick={reset}>Cancel</Button>
          </CardContent>
        </Card>
      )}

      {mode === 'review' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {imagePreview && (
            <Card className="border-gray-200">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-gray-500 mb-2">Scanned Image</p>
                <img src={imagePreview} alt="Scanned" className="w-full rounded-lg max-h-[250px] object-contain bg-gray-50" />
              </CardContent>
            </Card>
          )}
          <Card className="border-gray-200" data-testid="review-form">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-gray-700">
                  {scanning ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Scanning...</span> : 'Review & Save'}
                </p>
                <Badge className="bg-green-100 text-green-700 text-[9px]">{editData.source === 'qr_scan' ? 'QR Scan' : 'Card Scan'}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field icon={User} label="First Name *" field="first_name" placeholder="John" value={editData.first_name} onChange={handleFieldChange} />
                <Field icon={User} label="Last Name" field="last_name" placeholder="Doe" value={editData.last_name} onChange={handleFieldChange} />
                <Field icon={Mail} label="Email" field="email" placeholder="john@company.com" value={editData.email} onChange={handleFieldChange} />
                <Field icon={Phone} label="Phone" field="phone" placeholder="+1234567890" value={editData.phone} onChange={handleFieldChange} />
                <Field icon={Building2} label="Company" field="company" placeholder="Acme Inc." value={editData.company} onChange={handleFieldChange} />
                <Field icon={Briefcase} label="Job Title" field="job_title" placeholder="CEO" value={editData.job_title} onChange={handleFieldChange} />
                <Field icon={Globe} label="Website" field="website" placeholder="acme.com" value={editData.website} onChange={handleFieldChange} />
                <Field icon={MapPin} label="City" field="city" placeholder="New York" value={editData.city} onChange={handleFieldChange} />
                <Field icon={MapPin} label="Country" field="country" placeholder="United States" value={editData.country} onChange={handleFieldChange} />
                <Field icon={MapPin} label="Area/Region" field="area" placeholder="North America" value={editData.area} onChange={handleFieldChange} />
              </div>
              <div className="mt-3 flex gap-2">
                <Button onClick={saveContact} disabled={saving || scanning} className="bg-violet-600 hover:bg-violet-700 flex-1 gap-1" data-testid="save-contact-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {saving ? 'Saving...' : 'Save to CRM'}
                </Button>
                <Button variant="outline" onClick={reset}><RotateCcw className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
